import { emptyCellDash } from "src/components";
import { ChangeEventLineItem, ChangeEventLineItemType } from "src/generated/graphqlTypes";
import { ScopeChangesTable_LineItemTradeFragment } from "./components/ScopeChangesTable.generated";

export enum GuidedChangeEventStep {
  SelectConfiguration = "select-configuration",
  SelectOption = "select-option",
  SelectProductToReplace = "select-product-to-replace",
  ReviewConfiguration = "review-configuration",
  ReviewImpact = "review-impact",
  Review = "review",
}

export enum GuidedChangeEventConfigType {
  CheckoutConfig = "checkoutConfig",
  ProductOfferingConfig = "productOfferingConfig",
}

/** Returns the total added, total removed, and the number of added and removed items. */
export function celisImpactTotals(celis: readonly Pick<ChangeEventLineItem, "totalCostInCents" | "typeDetail">[]) {
  // The line items are deltas, so whether they add or remove money is just their sign.
  const [positiveCelis, negativeCelis] = celis.partition((li) => li.totalCostInCents > 0);
  const totalAddedInCents = positiveCelis.sum((li) => li.totalCostInCents);
  const totalRemovedInCents = Math.abs(negativeCelis.sum((li) => li.totalCostInCents));

  const addedItems = celis.count((li) => li.typeDetail.code === ChangeEventLineItemType.Add);
  const removedItems = celis.count((li) => li.typeDetail.code === ChangeEventLineItemType.Remove);

  return {
    totalAddedInCents,
    totalRemovedInCents,
    addedItems,
    removedItems,
    netItems: addedItems - removedItems,
    changedItems: addedItems + removedItems,
  };
}

/**
 * Shows a line item's trade the same way the change event line items table does, i.e.
 * "Dallas Plumbing (Revision 2) - Item Based" when a signed contract priced the line, or
 * "Dallas Plumbing (Not Contracted)" when the trade was picked by hand.
 */
export function tradeName(lineItem: ScopeChangesTable_LineItemTradeFragment | undefined): string {
  const bcli = lineItem?.proposedBidContractLineItem;
  if (bcli) {
    const { version, bidContract } = bcli.revision;
    return `${bidContract.displayName} (Revision ${version}) - ${bcli.ofIti ? "Plan Based" : "Item Based"}`;
  }
  if (lineItem?.tradePartner) return `${lineItem.tradePartner.name} (Not Contracted)`;
  return emptyCellDash;
}
